/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { Dispatch, FC, ReactNode, useEffect, useRef } from 'react';
import cx from 'classnames';
import { filter, find, get, isEmpty, map } from 'lodash';

import {
  Currency,
  SVG,
  Image,
  useFormatMessage
} from '@broadleaf/admin-components/dist/common';
import type {
  IActionComponent,
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import type {
  IOrder,
  IOrderFulfillment,
  IOrderFulfillmentItem,
  IOrderItem,
  IOrderLocalState
} from '@broadleaf/admin-components/dist/types/oms';

import type { ItemsOrderViewSectionComponents } from '../../..';

import messages from './FulfillmentCardBody.messages';
import { getFulfillmentStatuses } from '@broadleaf/admin-components/dist/oms/utils/OrderStatusUtils';
import { getCompletionPercentage } from '@broadleaf/admin-components/dist/oms/utils/FulfillmentUtils';
import betweenWith from '@broadleaf/admin-components/dist/common/utils/lodash/betweenWith';
import {
  findOrderItem,
  getThumbnailUrl
} from '@broadleaf/admin-components/dist/oms/utils/OrderItemUtils';
import { getAttribute } from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import PopoverLabel from '@broadleaf/admin-components/dist/oms/components/PopoverLabel';
import { CollectionStateHook } from '@broadleaf/admin-components/dist/collection/hooks/useCollectionState';
import {
  ItemName,
  ItemPromotions,
  ItemSku
} from '../../../../../FulfillmentView/FulfillmentItemSummary/FulfillmentItemSummary';

export interface FulfillmentCardBodyProps {
  actionComponents: Array<IActionComponent>;
  actionDefinitions: Array<IMetadataAction>;
  fulfillment: IOrderFulfillment;
  fulfillmentsState: CollectionStateHook;
  itemsMetadata: IMetadata;
  itemsViewComponents: ItemsOrderViewSectionComponents;
  orderDispatch: Dispatch<any>;
  orderMetadata: IMetadata;
  orderState: IOrderLocalState;
}

export interface FulfillmentCardBodyComponents {
  Items: FC<FulfillmentCardItemsProps>;
  Item: FC<FulfillmentCardItemProps>;
  Actions: FC<FulfillmentCardActionsProps>;
}

export interface FulfillmentCardBodyDefinition
  extends FC<FulfillmentCardBodyProps>,
    FulfillmentCardBodyComponents {}

export const FulfillmentCardBody: FulfillmentCardBodyDefinition = ({
  actionComponents,
  actionDefinitions,
  fulfillment,
  fulfillmentsState,
  itemsMetadata,
  itemsViewComponents,
  orderDispatch,
  orderMetadata,
  orderState
}) => {
  return (
    <div className="FulfillmentCardBody tw-flex tw-flex-col md:tw-flex-row">
      <div className="tw-flex tw-flex-auto tw-flex-col tw-p-4">
        <FulfillmentCardStatusIndicator
          orderMetadata={orderMetadata}
          fulfillment={fulfillment}
        />

        <itemsViewComponents.FulfillmentCardItems
          fulfillment={fulfillment}
          itemsMetadata={itemsMetadata}
          itemsViewComponents={itemsViewComponents}
          order={orderState.data}
        />
      </div>
      <div className="tw-flex tw-flex-1 tw-justify-center tw-border-t tw-bg-gray-100 tw-p-3 md:tw-max-w-xs md:tw-border-l md:tw-border-t-0">
        <itemsViewComponents.FulfillmentCardActions
          actionComponents={actionComponents}
          actionDefinitions={actionDefinitions}
          fulfillment={fulfillment}
          fulfillmentsState={fulfillmentsState}
          itemsMetadata={itemsMetadata}
          orderDispatch={orderDispatch}
          orderMetadata={orderMetadata}
          orderState={orderState}
        />
      </div>
    </div>
  );
};

export interface FulfillmentCardStatusIndicatorProps {
  orderMetadata: IMetadata;
  fulfillment: IOrderFulfillment;
}

export const FulfillmentCardStatusIndicator: FC<FulfillmentCardStatusIndicatorProps> = ({
  orderMetadata,
  fulfillment
}) => {
  const { status } = fulfillment;
  const fulfillmentStatuses = getFulfillmentStatuses(orderMetadata);
  const { label, color } = find(fulfillmentStatuses, {
    value: status
  }) || { label: status, color: 'gray' };
  const completionPercentage = getCompletionPercentage(status);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (barRef.current) {
      // csp header workaround
      barRef.current.style.width = completionPercentage;
    }
  }, [completionPercentage]);

  return (
    <div className="tw-relative tw-w-full tw-py-1">
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        <div>
          <span
            className={`tw-inline-block tw-rounded-full tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-text-${color}-600 tw-bg-${color}-200`}
          >
            {label}
          </span>
        </div>
      </div>
      <div
        className={`tw-mb-4 tw-flex tw-h-2 tw-overflow-hidden tw-rounded tw-text-xs tw-bg-${color}-200`}
      >
        <div
          ref={barRef}
          className={`tw-flex tw-flex-col tw-justify-center tw-whitespace-nowrap tw-text-center tw-text-white tw-shadow-none tw-bg-${color}-500`}
        />
      </div>
    </div>
  );
};

export interface FulfillmentCardItemsProps {
  fulfillment: IOrderFulfillment;
  itemsMetadata: IMetadata;
  itemsViewComponents: any;
  order: IOrder;
}

export const FulfillmentCardItems: FC<FulfillmentCardItemsProps> = ({
  fulfillment,
  itemsMetadata,
  itemsViewComponents,
  order
}) => {
  const { fulfillmentItems = [] } = fulfillment;
  return (
    <div>
      {betweenWith(
        map(fulfillmentItems, fulfillmentItem => {
          const orderItem = findOrderItem(order.orderItems, {
            id: fulfillmentItem.orderItemId
          });
          return (
            <itemsViewComponents.FulfillmentCardItem
              key={fulfillmentItem.id}
              itemsMetadata={itemsMetadata}
              itemsViewComponents={itemsViewComponents}
              fulfillmentItem={fulfillmentItem}
              orderItem={orderItem}
            />
          );
        }),
        (_: any, ___: any, index: any) => (
          <hr key={`hr-${index}`} className="tw-my-4 tw-border-gray-300" />
        )
      )}
    </div>
  );
};

export interface FulfillmentCardItemProps {
  fulfillmentItem: IOrderFulfillmentItem;
  itemsMetadata: IMetadata;
  orderItem: IOrderItem;
}

export const FulfillmentCardItem: FC<FulfillmentCardItemProps> = ({
  fulfillmentItem,
  itemsMetadata,
  orderItem
}) => {
  const thumbnailUrl = getThumbnailUrl(orderItem);
  const distinguishPriceType = getAttribute(
    itemsMetadata,
    'distinguishPriceType',
    false
  );
  fulfillmentItem.orderItem = orderItem;
  const priceInfoPath = getAttribute(itemsMetadata, 'priceInfoPath');
  return (
    <div className="tw-flex tw-flex-col tw-items-center md:tw-flex-row md:tw-items-start">
      <div className="tw-mb-3 tw-h-32 tw-w-32 tw-flex-initial md:tw-mb-0 md:tw-h-24 md:tw-w-24">
        <Image
          alt={orderItem.name}
          className="tw-max-h-full tw-w-full"
          fallback={
            <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-bg-gray-100">
              <SVG
                className="tw-h-1/2 tw-w-1/2 tw-fill-current tw-text-gray-400"
                name="photo"
              />
            </div>
          }
          src={thumbnailUrl}
        />
      </div>
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-row tw-overflow-hidden md:tw-ml-3 md:tw-w-auto">
        <div className="tw-flex-1 tw-overflow-hidden">
          <ItemName fulfillmentItem={fulfillmentItem} />
          <ItemSku fulfillmentItem={fulfillmentItem} />
          <ItemPromotions fulfillmentItem={fulfillmentItem} />
        </div>
        <div className="tw-ml-3 tw-flex-initial">
          <FulfillmentItemTotal fulfillmentItem={fulfillmentItem} />
        </div>
      </div>
    </div>
  );
};

export interface FulfillmentItemPriceProps {
  /**
   * The price type to display in a more prominent manner, e.g., `salePrice`.
   * This is provided by the `priceInfo` on the `orderItem`.
   * By default this only modifies the display if `distinguishPriceType === 'salePrice'`.
   */
  distinguishPriceType: string;
  /**
   * The property path of the price info of the order item
   */
  priceInfoPath: string;
  fulfillmentItem: IOrderFulfillmentItem;
  orderItem: IOrderItem;
}

export const FulfillmentItemPrice: FC<FulfillmentItemPriceProps> = ({
  distinguishPriceType,
  priceInfoPath,
  fulfillmentItem,
  orderItem
}) => {
  const priceInfo = get(
    orderItem,
    priceInfoPath,
    get(fulfillmentItem, priceInfoPath)
  );
  const { unitPrice, unitPriceType } = orderItem;
  const distinguishSalePrice =
    distinguishPriceType && unitPriceType === 'salePrice';
  const basePrice = get(
    priceInfo,
    'priceTypeDetails.standardPrice.bestPrice',
    get(priceInfo, 'priceTypeDetails.basePrice.bestPrice')
  );
  return (
    <div className="tw-mt-1 tw-text-sm tw-text-gray-700 md:tw-text-base">
      <span
        className={cx({
          'tw-font-medium tw-text-red-600': distinguishSalePrice
        })}
      >
        <Currency currency={unitPrice.currency} value={unitPrice.amount} />
      </span>{' '}
      {distinguishSalePrice && !!basePrice && (
        <span className="tw-text-xs tw-line-through md:tw-text-sm">
          <Currency currency={basePrice.currency} value={basePrice.amount} />
        </span>
      )}
      {fulfillmentItem.quantity > 1 && ` x ${fulfillmentItem.quantity}`}
    </div>
  );
};

export interface FulfillmentItemTotalProps {
  fulfillmentItem: IOrderFulfillmentItem;
}

export const FulfillmentItemTotal: FC<FulfillmentItemTotalProps> = ({
  fulfillmentItem
}) => {
  const formatMessage = useFormatMessage();
  const {
    merchandiseAdjustmentsTotal,
    merchandiseSubtotal,
    merchandiseTaxTotal,
    proratedOrderAdjustments
  } = fulfillmentItem;

  const itemTotal = getItemTotal(fulfillmentItem);
  return (
    <PopoverLabel
      label={
        <Currency currency={itemTotal.currency} value={itemTotal.amount} />
      }
      position="bottom-right"
    >
      <div className="tw-p-2">
        <FulfillmentItemTotalDetail
          className="tw-mb-1 tw-text-sm"
          label={formatMessage(messages.merchandiseSubtotal)}
        >
          <Currency
            currency={merchandiseSubtotal.currency}
            value={merchandiseSubtotal.amount}
          />
        </FulfillmentItemTotalDetail>

        {!!merchandiseAdjustmentsTotal &&
          merchandiseAdjustmentsTotal.amount > 0 && (
            <FulfillmentItemTotalDetail
              className="tw-mb-1 tw-text-sm tw-text-red-600"
              label={formatMessage(messages.merchandiseAdjustmentsTotal)}
            >
              <Currency
                currency={merchandiseAdjustmentsTotal.currency}
                value={-merchandiseAdjustmentsTotal.amount}
              />
            </FulfillmentItemTotalDetail>
          )}

        {!!proratedOrderAdjustments && proratedOrderAdjustments.amount > 0 && (
          <FulfillmentItemTotalDetail
            className="tw-mb-1 tw-text-sm tw-text-red-600"
            label={formatMessage(messages.proratedOrderAdjustments)}
          >
            <Currency
              currency={proratedOrderAdjustments.currency}
              value={-proratedOrderAdjustments.amount}
            />
          </FulfillmentItemTotalDetail>
        )}

        <FulfillmentItemTotalDetail
          className="tw-mb-1 tw-text-sm"
          label={formatMessage(messages.merchandiseTaxTotal)}
        >
          <Currency
            currency={merchandiseTaxTotal.currency}
            value={merchandiseTaxTotal.amount}
          />
        </FulfillmentItemTotalDetail>
      </div>
      <hr className="tw-border-gray-300" />
      <div className="tw-p-2">
        <FulfillmentItemTotalDetail
          className="tw-text-base"
          label={formatMessage(messages.merchandiseTotal)}
        >
          <Currency currency={itemTotal.currency} value={itemTotal.amount} />
        </FulfillmentItemTotalDetail>
      </div>
    </PopoverLabel>
  );
};

function getItemTotal(fulfillmentItem) {
  const {
    merchandiseTaxTotal,
    merchandiseTotal,
    proratedOrderAdjustments,
    includedMerchandiseTax
  } = fulfillmentItem;

  let totalAmount = isEmpty(proratedOrderAdjustments)
    ? merchandiseTotal.amount
    : merchandiseTotal.amount - proratedOrderAdjustments.amount;

  totalAmount = isEmpty(merchandiseTaxTotal)
    ? totalAmount
    : totalAmount + merchandiseTaxTotal.amount;

  totalAmount = isEmpty(includedMerchandiseTax)
    ? totalAmount
    : totalAmount - includedMerchandiseTax.amount;

  return {
    currency: merchandiseTotal.currency,
    amount: totalAmount
  };
}

export interface FulfillmentItemPromotionsAppliedProps {
  fulfillmentItem: IOrderFulfillmentItem;
  orderItem: IOrderItem;
}

export const FulfillmentItemPromotionsApplied: FC<FulfillmentItemPromotionsAppliedProps> = ({
  fulfillmentItem,
  orderItem
}) => {
  const formatMessage = useFormatMessage();
  const { itemAdjustments = [] } = orderItem;
  const { merchandiseAdjustmentsTotal } = fulfillmentItem;
  return (
    <div className="tw-mt-2">
      <PopoverLabel
        label={formatMessage(messages.promotionsApplied)}
        position="bottom-left"
      >
        <div className="tw-p-2">
          {itemAdjustments.map((itemAdjustment, index) => {
            const { amount, codeUsed, offerRef } = itemAdjustment;
            const label = codeUsed || (offerRef && offerRef.name) || '---';
            return (
              <FulfillmentItemTotalDetail
                key={index}
                className="tw-mb-1 tw-text-xs"
                label={label}
              >
                <Currency currency={amount.currency} value={amount.amount} />
              </FulfillmentItemTotalDetail>
            );
          })}
        </div>
        <hr className="tw-border-gray-300" />
        <div className="tw-p-2">
          <FulfillmentItemTotalDetail
            className="tw-text-sm"
            label={formatMessage(messages.merchandiseAdjustmentsTotal)}
          >
            <Currency
              currency={merchandiseAdjustmentsTotal.currency}
              value={merchandiseAdjustmentsTotal.amount}
            />
          </FulfillmentItemTotalDetail>
        </div>
      </PopoverLabel>
    </div>
  );
};

export interface FulfillmentItemAttributeChoicesProps {
  orderItem: IOrderItem;
}

export const FulfillmentItemAttributeChoices: FC<FulfillmentItemAttributeChoicesProps> = ({
  orderItem
}) => {
  const { attributeChoices } = orderItem;
  if (isEmpty(attributeChoices)) {
    return null;
  }
  return (
    <ul className="tw-mt-2 tw-list-none tw-text-sm">
      {map(attributeChoices, (attributeChoice, attributeName) => (
        <li key={attributeName}>
          <span
            className="tw-font-semibold tw-text-gray-700"
            title={attributeChoice.optionLabel || attributeName}
          >{`${attributeChoice.optionLabel || attributeName}: `}</span>
          <span
            className="tw-font-medium tw-text-gray-600"
            title={attributeChoice.label || attributeChoice.value}
          >
            {attributeChoice.label || attributeChoice.value}
          </span>
        </li>
      ))}
    </ul>
  );
};

export interface FulfillmentItemTotalDetailProps {
  className?: string;
  label: ReactNode;
}

export const FulfillmentItemTotalDetail: FC<FulfillmentItemTotalDetailProps> = ({
  children,
  className,
  label
}) => {
  return (
    <div className={cx(className, 'tw-flex tw-justify-between')}>
      <div className="tw-text-gray-700">{label}</div>
      <div className="tw-ml-2 tw-font-medium tw-text-gray-900">{children}</div>
    </div>
  );
};

export interface FulfillmentCardActionsProps {
  actionComponents: Array<IActionComponent>;
  actionDefinitions: Array<IMetadataAction>;
  fulfillment: IOrderFulfillment;
  fulfillmentsState: CollectionStateHook;
  itemsMetadata: IMetadata;
  orderDispatch: Dispatch<any>;
  orderMetadata: IMetadata;
  orderState: IOrderLocalState;
}

export const FulfillmentCardActions: FC<FulfillmentCardActionsProps> = ({
  actionComponents,
  actionDefinitions,
  fulfillment,
  fulfillmentsState,
  itemsMetadata,
  orderDispatch,
  orderMetadata,
  orderState
}) => {
  const cardActionDefinitions = filter(actionDefinitions, {
    placement: 'CARD'
  });
  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-items-center md:tw-w-56">
      {map(cardActionDefinitions, (cardActionDefinition, index) => {
        const actionComponent = find(actionComponents, {
          placement: cardActionDefinition.placement,
          type: cardActionDefinition.type
        });
        if (!actionComponent) {
          return null;
        }
        return (
          <actionComponent.component
            key={index}
            actionDefinition={cardActionDefinition}
            fulfillment={fulfillment}
            fulfillmentsState={fulfillmentsState}
            itemsMetadata={itemsMetadata}
            orderDispatch={orderDispatch}
            orderMetadata={orderMetadata}
            orderState={orderState}
          />
        );
      })}
    </div>
  );
};

FulfillmentCardBody.Items = FulfillmentCardItems;
FulfillmentCardBody.Item = FulfillmentCardItem;
FulfillmentCardBody.Actions = FulfillmentCardActions;

export default FulfillmentCardBody;
